import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { postApi } from "../../../api/getUser";
import useClickAway from "../../custom-hooks/useClickAway";
import { fetchServiceTaskTemplates } from "../../../features/servicesTasks/servicesTasksSlice";
import { updateServiceTaskOptions } from "../../../components/Services/ServicesFormNew";
import { DeletePopup } from "../table/components/DeletePopup";
import ErrorPopup from "../ErrorPopup";
import ServiceTasksFormPopup from "../../../components/Services/ServiceTasksFormPopup";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
  FilterButton,
} from "../TableElements";
import Error from "./Error";
import {
  colorLightGray,
  colorTextNew,
  colorAdditional,
  colorGray,
  buttonColorNew,
  scrollbarStyles,
} from "../../../utils/palette";

const ServiceTaskTemplatePopupNew = ({
  showPopup,
  setShowPopup,
  editData,
  serviceTaskOptions,
  newTemplateName,
  setServiceTaskOptions,
  selectedVehicleId,
  onClose,
  onDeleteSuccess: onDeleteSuccessProp,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  const [templateName, setTemplateName] = useState(newTemplateName || "");
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrPopup, setShowErrPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const { userId } = useSelector((state) => state.user);
  const { templates } = useSelector((state) => state.servicesTasks);

  const [isTemplateNameFocused, setIsTemplateNameFocused] = useState(false);
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const templateNameRef = useRef(null);
  const kebabMenuRef = useClickAway({
    handleCloseElement: () => setShowKebabMenu(false),
  });
  const newTaskInfo = useRef("");
  const [showAddServiceTaskFormPopup, setShowAddServiceTaskFormPopup] =
    useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [editPartsLaborTask, setEditPartsLaborTask] = useState(null);
  const [showEditServiceTaskFormPopup, setShowEditServiceTaskFormPopup] =
    useState(false);

  const vehiclesList = useSelector((state) => state.vehicles.list);
  //Loop through vehicles list, match vehicle id with selectedVehicleId, if asset type is 1 (equipment) then set isEquipment to true, else false. If selectedVehicleId is "All", then set isEquipment to true if any vehicle in the list has asset type 1
  const isEquipment = useMemo(() => {
    const selectedVehicle = vehiclesList.find(
      (v) => v.veh_id === selectedVehicleId,
    );
    return selectedVehicle ? selectedVehicle.asset_type === 1 : false;
  }, [selectedVehicleId, vehiclesList]);

  useEffect(() => {
    if (editData) {
      setTemplateName(editData.label);
      const tasks =
        editData.taskIds
          ?.map((id) =>
            serviceTaskOptions?.find((t) => t.service_task_id === id),
          )
          .filter(Boolean) || [];
      setSelectedTasks(tasks);
    } else {
      setTemplateName(newTemplateName || "");
      setSelectedTasks([]);
    }
    setExpandedTaskId(null);
    setEditPartsLaborTask(null);
  }, [editData, newTemplateName]);

  // Group flat serviceTaskOptions by category; uncategorized goes last
  const groupedTaskOptions = useMemo(() => {
    const groups = {};
    const uncategorized = [];
    serviceTaskOptions?.forEach((task) => {
      const catId = task.st_category_id;
      const catName = task.category_name;
      if (!catId && !catName) {
        uncategorized.push(task);
      } else {
        if (!groups[catId]) {
          groups[catId] = { label: catName, catId, options: [] };
        }
        groups[catId].options.push(task);
      }
    });
    const result = Object.values(groups);
    if (uncategorized.length > 0) {
      result.push({
        label: t("uncategorized"),
        catId: "uncategorized",
        options: uncategorized,
      });
    }
    return result;
  }, [serviceTaskOptions, t]);

  // Filter groups by search query (matches task name or category name)
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedTaskOptions;
    const query = searchQuery.toLowerCase();
    return groupedTaskOptions
      .map((group) => ({
        ...group,
        options: group.options.filter(
          (task) =>
            task.service_task_name?.toLowerCase().includes(query) ||
            group.label?.toLowerCase().includes(query),
        ),
      }))
      .filter((group) => group.options.length > 0);
  }, [groupedTaskOptions, searchQuery]);

  const isTaskSelected = (task) =>
    selectedTasks.some((t) => t.service_task_id === task.service_task_id);

  const toggleTask = (task) => {
    setErrors((prev) => ({ ...prev, tasks: null }));
    if (isTaskSelected(task)) {
      setSelectedTasks((prev) =>
        prev.filter((t) => t.service_task_id !== task.service_task_id),
      );
    } else {
      setSelectedTasks((prev) => [...prev, task]);
    }
  };

  const toggleCategoryCollapse = (catId) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const getCategorySelectionState = (group) => {
    const selectedCount = group.options.filter((task) =>
      isTaskSelected(task),
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === group.options.length) return "all";
    return "some";
  };

  const toggleCategorySelection = (e, group) => {
    e.stopPropagation();
    setErrors((prev) => ({ ...prev, tasks: null }));
    const state = getCategorySelectionState(group);
    if (state === "all") {
      // Deselect all tasks in this category
      const taskIds = new Set(group.options.map((t) => t.service_task_id));
      setSelectedTasks((prev) =>
        prev.filter((t) => !taskIds.has(t.service_task_id)),
      );
    } else {
      // Select all tasks in this category that aren't already selected
      const toAdd = group.options.filter((task) => !isTaskSelected(task));
      setSelectedTasks((prev) => [...prev, ...toAdd]);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const reordered = Array.from(selectedTasks);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setSelectedTasks(reordered);
  };

  const handleClose = () => {
    setShowPopup(false);
    setTemplateName("");
    setSelectedTasks([]);
    setErrors({});
    setSearchQuery("");
    setExpandedTaskId(null);
    setEditPartsLaborTask(null);
    if (onClose) onClose();
  };

  const handleSave = async () => {
    try {
      const newErrors = {};
      if (!templateName.trim()) {
        newErrors.name = t("required_err");
      }
      if (selectedTasks.length === 0) {
        newErrors.tasks = t("invalid_st_msg");
      }

      const duplicateTemplate = templates?.some(
        (template) =>
          template.label.trim().toLowerCase() ===
            templateName.trim().toLowerCase() &&
          template.template_id !== editData?.template_id,
      );
      if (duplicateTemplate) {
        newErrors.name = t("dup_template_name");
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setErrors({});
      setSaveLoading(true);

      const templateId = editData
        ? editData.template_id
        : `${userId}_${Date.now()}`;
      const selectedTaskIds = selectedTasks.map((task) => task.service_task_id);

      const templatePayload = {
        template_id: templateId,
        template_name: templateName,
        template_task_ids: selectedTaskIds,
        querystring: "addEditServiceTaskTemplate",
        action: editData ? 2 : 1,
        operation: editData ? "update" : "create",
        sync_version: "v2",
        source: "web",
      };

      const templateRes = await postApi(templatePayload, "workOrder");
      if (templateRes?.success) {
        dispatch(
          fetchServiceTaskTemplates({
            querystring: "fetch_all_service_task_templates",
          }),
        );
      }
      setSaveLoading(false);
      handleClose();
    } catch (error) {
      setSaveLoading(false);
      setShowErrPopup(true);
    }
  };

  const handleCreate = (value) => {
    newTaskInfo.current = { taskName: value, vehicleId: "All" };
    setShowAddServiceTaskFormPopup(true);
  };

  const onDeleteSuccess = () => {
    dispatch(
      fetchServiceTaskTemplates({
        querystring: "fetch_all_service_task_templates",
      }),
    );
    if (onDeleteSuccessProp) onDeleteSuccessProp(editData.template_id);
    handleClose();
  };

  const formatSecondsToLaborTime = (totalSeconds) => {
    if (!totalSeconds) return "";
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h === 0 && m === 0) return "";
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const handleExpandTask = (taskId) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  const handleEditPartsLabor = (task) => {
    const {
      service_task_id,
      service_task_name,
      estimated_time,
      suggestedParts,
      comments,
      st_category_id,
      category_name,
      recurring,
      servicetask_veh_id,
    } = task;
    setEditPartsLaborTask({
      service_task_id,
      service_task_name,
      estimated_time,
      suggestedParts,
      comments: comments || "",
      st_category_id: st_category_id || "",
      category_name: category_name || "",
      recurring: recurring || 0,
      servicetask_veh_id: servicetask_veh_id || "All",
    });
    setShowEditServiceTaskFormPopup(true);
  };

  const refreshServiceTaskOptions = async () => {
    const apiData = {
      querystring: "getallservicetask",
      service_veh_id: selectedVehicleId || "All",
    };
    const serviceTasksRes = await updateServiceTaskOptions(apiData);
    setServiceTaskOptions(serviceTasksRes);
    return serviceTasksRes;
  };

  const handleServiceTaskAdded = async (newServiceTaskId) => {
    const serviceTasksRes = await refreshServiceTaskOptions();
    const newTask = serviceTasksRes.find(
      (t) => t.service_task_id === newServiceTaskId,
    );
    if (
      newTask &&
      !selectedTasks.find((t) => t.service_task_id === newTask.service_task_id)
    ) {
      setSelectedTasks((prev) => [...prev, newTask]);
      newTaskInfo.current = null;
    }
  };

  const handleServiceTaskEdited = async (savedTaskId) => {
    const serviceTasksRes = await refreshServiceTaskOptions();
    const freshTask = serviceTasksRes.find(
      (t) => t.service_task_id === savedTaskId,
    );
    if (freshTask) {
      setSelectedTasks((prev) =>
        prev.map((t) => (t.service_task_id === savedTaskId ? freshTask : t)),
      );
    }
    setEditPartsLaborTask(null);
  };

  return (
    <>
      {showAddServiceTaskFormPopup && (
        <ServiceTasksFormPopup
          showPopup={showAddServiceTaskFormPopup}
          setShowPopup={setShowAddServiceTaskFormPopup}
          //newTaskInfo={newTaskInfo}
          newTaskName={newTaskInfo.current.taskName}
          vehicleId={selectedVehicleId}
          onServiceTaskSaved={handleServiceTaskAdded}
          assetIsEquipment={isEquipment}
        />
      )}
      {showEditServiceTaskFormPopup && (
        <ServiceTasksFormPopup
          showPopup={showEditServiceTaskFormPopup}
          setShowPopup={(val) => {
            setShowEditServiceTaskFormPopup(val);
            if (!val) setEditPartsLaborTask(null);
          }}
          editPartsLaborMode
          vehicleId={selectedVehicleId}
          editData={editPartsLaborTask}
          onServiceTaskSaved={handleServiceTaskEdited}
          assetIsEquipment={isEquipment}
        />
      )}
      {showDeletePopup && editData && (
        <DeletePopup
          showPopup={showDeletePopup}
          setShowPopup={setShowDeletePopup}
          deleteApiData={{
            delete_ids: editData.template_id,
            querystring: "multiple_delete",
            sub_query_string: "ST_TEMPLATE",
          }}
          screen="reporting"
          onDeleteSuccess={onDeleteSuccess}
        />
      )}
      <Modal
        show={showPopup && !editPartsLaborTask && !showAddServiceTaskFormPopup}
        onHide={handleClose}
        size="xl"
        backdropClassName="custom-backdrop"
      >
        <ModalHeaderStyled>
          <div style={{ flex: 1, minWidth: 0, maxWidth: "420px" }}>
            <TemplateSublabel>{t("maintenance_template")}</TemplateSublabel>
            <TemplateNameInputRow>
              <TemplateNameInput
                ref={templateNameRef}
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  if (e.target.value.trim())
                    setErrors((prev) => ({ ...prev, name: null }));
                }}
                onFocus={() => setIsTemplateNameFocused(true)}
                onBlur={() => setIsTemplateNameFocused(false)}
                placeholder={t("template_name_placeholder")}
                isFocused={isTemplateNameFocused}
              />
              <EditIconButton
                type="button"
                onClick={() => {
                  templateNameRef.current?.focus();
                }}
              >
                <i className="fi fi-rr-pencil" style={{ fontSize: "13px" }} />
              </EditIconButton>
            </TemplateNameInputRow>
            {errors.name && <Error error={errors.name} />}
          </div>
          <HeaderActions>
            {editData && (
              <KebabMenuWrapper ref={kebabMenuRef}>
                <KebabButton
                  type="button"
                  onClick={() => setShowKebabMenu((prev) => !prev)}
                >
                  <i className="fi fi-rr-menu-dots-vertical" />
                </KebabButton>
                {showKebabMenu && (
                  <KebabDropdown>
                    <KebabItem
                      onClick={() => {
                        setShowKebabMenu(false);
                        setShowDeletePopup(true);
                      }}
                      isDestructive
                    >
                      <i className="fi fi-rr-trash" />
                      {t("delete_template")}
                    </KebabItem>
                  </KebabDropdown>
                )}
              </KebabMenuWrapper>
            )}
            <FilterButton type="button" onClick={handleClose}>
              {t("cancel")}
            </FilterButton>
            <AddFormButton
              type="button"
              onClick={handleSave}
              disabled={saveLoading || selectedTasks.length === 0}
            >
              {saveLoading ? <CustomLoader /> : t("save_template")}
            </AddFormButton>
          </HeaderActions>
        </ModalHeaderStyled>

        <ModalBodyStyled>
          <LeftPanel>
            <PanelHeader
              style={{
                borderBottom: `1px solid ${colorLightGray}`,
              }}
            >
              {t("task_library")}
            </PanelHeader>

            <SearchContainer>
              <i
                className="fi fi-rr-search"
                style={{ color: colorAdditional, fontSize: "13px" }}
              />
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("search_tasks")}
              />
            </SearchContainer>

            <CreateTaskLink onClick={() => handleCreate("")}>
              <i
                className="fi fi-rr-plus"
                style={{ fontSize: "12px", marginTop: "2px" }}
              />
              <span>{t("create_new_service_task")}</span>
            </CreateTaskLink>

            <TaskListScroll>
              {filteredGroups.map((group) => {
                const isCollapsed = collapsedCategories.has(group.catId);
                const selectionState = getCategorySelectionState(group);
                return (
                  <div key={group.catId}>
                    <CategoryHeader
                      onClick={() => toggleCategoryCollapse(group.catId)}
                    >
                      <CategoryCheckbox
                        type="checkbox"
                        checked={selectionState === "all"}
                        ref={(el) => {
                          if (el) el.indeterminate = selectionState === "some";
                        }}
                        onChange={() => {}}
                        onClick={(e) => toggleCategorySelection(e, group)}
                      />
                      <CategoryName>{group.label}</CategoryName>
                      <CategoryCount>{group.options.length}</CategoryCount>
                      <span style={{ flex: 1 }} />
                      <i
                        className={`fi ${isCollapsed ? "fi-rr-angle-small-right" : "fi-rr-angle-small-down"}`}
                        style={{
                          fontSize: "12px",
                          color: colorAdditional,
                          position: "relative",
                          top: "2px",
                        }}
                      />
                    </CategoryHeader>
                    {!isCollapsed &&
                      group.options.map((task) => (
                        <TaskItem
                          key={task.service_task_id}
                          onClick={() => toggleTask(task)}
                        >
                          <TaskCheckbox
                            type="checkbox"
                            checked={isTaskSelected(task)}
                            onChange={() => {}}
                          />
                          <TaskItemLabel>
                            {task.service_task_name}
                          </TaskItemLabel>
                        </TaskItem>
                      ))}
                  </div>
                );
              })}
              {filteredGroups.length === 0 && (
                <EmptyText>{t("no_data_available")}</EmptyText>
              )}
            </TaskListScroll>
            {errors.tasks && (
              <div style={{ padding: "0 16px" }}>
                <Error error={errors.tasks} />
              </div>
            )}
          </LeftPanel>

          <Divider />

          <RightPanel>
            <PanelHeaderRow>
              <PanelHeader style={{ marginBottom: 0 }}>
                {t("task_sequence")}
              </PanelHeader>
              {selectedTasks.length > 0 && (
                <TaskCountBadge>
                  {t("count_tasks", { count: selectedTasks.length })}
                </TaskCountBadge>
              )}
            </PanelHeaderRow>

            <SequenceColumnHeader>
              <span style={{ width: "20px", flexShrink: 0 }} />
              <span style={{ width: "24px", flexShrink: 0 }}>#</span>
              <span style={{ flex: 1 }}>{t("service_task_name")}</span>
              <span style={{ width: "130px", flexShrink: 0 }}>
                {t("suggested_parts")}
              </span>
              <span style={{ width: "110px", flexShrink: 0 }}>
                {t("est_labor_time")}
              </span>
              <span style={{ width: "32px", flexShrink: 0 }} />
              <span style={{ width: "32px", flexShrink: 0 }} />
            </SequenceColumnHeader>

            <SequenceScroll>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="task-sequence">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {selectedTasks.map((task, index) => {
                        const suggestedParts = task.suggestedParts || [];
                        const laborTimeLabel = formatSecondsToLaborTime(
                          task.estimated_time,
                        );
                        return (
                          <Draggable
                            key={task.service_task_id}
                            draggableId={`seq-${task.service_task_id}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <SequenceItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                isDragging={snapshot.isDragging}
                              >
                                <SequenceRow
                                  {...provided.dragHandleProps}
                                  onClick={() =>
                                    handleExpandTask(task.service_task_id)
                                  }
                                >
                                  <DragHandle>
                                    <i
                                      className="fi fi-rr-grip-dots-vertical"
                                      style={{
                                        color: colorGray,
                                        fontSize: "10px",
                                        position: "relative",
                                        top: "1px",
                                      }}
                                    />
                                  </DragHandle>
                                  <SequenceNumber>{index + 1}</SequenceNumber>
                                  <SequenceTaskName style={{ flex: 1 }}>
                                    {task.service_task_name || task.label}
                                  </SequenceTaskName>
                                  <SequencePartsCol>
                                    {suggestedParts.length > 0
                                      ? t("count_parts", {
                                          count: suggestedParts.length,
                                        })
                                      : "—"}
                                  </SequencePartsCol>
                                  <SequenceLaborCol>
                                    {laborTimeLabel || "—"}
                                  </SequenceLaborCol>
                                  <ChevronButton
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleExpandTask(task.service_task_id);
                                    }}
                                  >
                                    <i
                                      className={`fi fi-rr-angle-small-${expandedTaskId === task.service_task_id ? "down" : "right"}`}
                                      style={{
                                        fontSize: "14px",
                                        position: "relative",
                                        top: "2px",
                                      }}
                                    />
                                  </ChevronButton>
                                  <ActionIconContainer
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleTask(task);
                                    }}
                                    style={{
                                      flexShrink: 0,
                                      alignSelf: "center",
                                    }}
                                  >
                                    <i className="fi fi-rr-cross-small" />
                                  </ActionIconContainer>
                                </SequenceRow>
                                {expandedTaskId === task.service_task_id && (
                                  <TaskExpandedSection>
                                    <TaskExpandedSubLabel>
                                      {t("suggested_parts")}
                                    </TaskExpandedSubLabel>
                                    {suggestedParts.length > 0 ? (
                                      <SuggestedPartsTable>
                                        <thead>
                                          <tr>
                                            <SuggestedPartsHeaderCell>
                                              {t("part_name")}
                                            </SuggestedPartsHeaderCell>
                                            <SuggestedPartsHeaderCell>
                                              {t("part_no")}
                                            </SuggestedPartsHeaderCell>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {suggestedParts.map((p, i) => (
                                            <tr key={p.part_id || i}>
                                              <SuggestedPartsDataCell>
                                                {p.part_name}
                                              </SuggestedPartsDataCell>
                                              <SuggestedPartsDataCell>
                                                {p.part_number || "—"}
                                              </SuggestedPartsDataCell>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </SuggestedPartsTable>
                                    ) : (
                                      <TaskExpandedEmpty>
                                        {t("no_parts_added_yet")}
                                      </TaskExpandedEmpty>
                                    )}
                                    <TaskExpandedSubLabel
                                      style={{ marginTop: "10px" }}
                                    >
                                      {t("est_labor_time")}
                                    </TaskExpandedSubLabel>
                                    <TaskExpandedValue>
                                      {laborTimeLabel || t("not_set")}
                                    </TaskExpandedValue>
                                    <EditPartsLaborLink
                                      onClick={() => handleEditPartsLabor(task)}
                                    >
                                      <i
                                        className="fi fi-rr-pencil"
                                        style={{
                                          fontSize: "11px",
                                          position: "relative",
                                          top: "1px",
                                        }}
                                      />
                                      {t("edit_parts_labor_time")}
                                    </EditPartsLaborLink>
                                  </TaskExpandedSection>
                                )}
                              </SequenceItem>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {selectedTasks.length === 0 && (
                <EmptyText style={{ textAlign: "center", marginTop: "32px" }}>
                  {t("no_tasks_selected")}
                </EmptyText>
              )}
            </SequenceScroll>

            <DragHint>
              <i
                className="fi fi-rr-arrows-retweet"
                style={{ fontSize: "11px" }}
              />
              <span>{t("drag_to_reorder")}</span>
            </DragHint>
          </RightPanel>
        </ModalBodyStyled>
      </Modal>

      {showErrPopup && (
        <ErrorPopup showPopup={showErrPopup} setShowPopup={setShowErrPopup} />
      )}
    </>
  );
};

export default ServiceTaskTemplatePopupNew;

// ─── Styled Components ───────────────────────────────────────────────────────

const ModalHeaderStyled = styled(Modal.Header)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid ${colorLightGray};
`;

const TemplateSublabel = styled.p`
  font-size: 11px;
  font-weight: 500;
  color: ${colorAdditional};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 4px 0;
`;

const TemplateNameInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TemplateNameInput = styled.input`
  font-size: 16px;
  font-weight: 600;
  color: ${colorTextNew};
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.isFocused ? buttonColorNew : "transparent")};
  outline: none;
  background: transparent;
  flex: 1;
  padding: 0 0 2px 0;
  transition: border-color 0.15s ease;
  &::placeholder {
    color: ${colorLightGray};
    font-weight: 400;
  }
`;

const EditIconButton = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: ${colorAdditional};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:hover {
    color: ${buttonColorNew};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
  padding-top: 4px;
`;

const KebabMenuWrapper = styled.div`
  position: relative;
`;

const KebabButton = styled.button`
  background: none;
  border: 1px solid ${colorLightGray};
  border-radius: 5px;
  padding: 4px 8px;
  cursor: pointer;
  color: ${colorAdditional};
  display: flex;
  align-items: center;
  font-size: 14px;
  &:hover {
    background: #f0f2f4;
  }

  i {
    position: relative;
    top: 2px;
  }
`;

const KebabDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid ${colorLightGray};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 100;
  overflow: hidden;
`;

const KebabItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 13px;
  cursor: pointer;
  color: ${(props) => (props.isDestructive ? "#dc3545" : colorTextNew)};
  &:hover {
    background: #f8f9fa;
  }
  i {
    position: relative;
    top: 1px;
  }
`;

const ModalBodyStyled = styled(Modal.Body)`
  display: flex;
  padding: 0;
  height: calc(var(--window-height, 100vh) - 220px);
  min-height: 400px;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  width: 40%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Divider = styled.div`
  width: 1px;
  background: ${colorLightGray};
  flex-shrink: 0;
`;

const PanelHeader = styled.p`
  font-size: 11px;
  font-weight: 500;
  color: ${colorAdditional};
  background: #f8f9fa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px 8px;
  margin: 0;
  //border-bottom: 1px solid ${colorLightGray};
`;

const PanelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  background: #f8f9fa;
  justify-content: space-between;
  padding-right: 16px;
  border-bottom: 1px solid ${colorLightGray};
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid ${colorLightGray};
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 13px;
  color: ${colorTextNew};
  flex: 1;
  background: transparent;
  &::placeholder {
    color: ${colorAdditional};
  }
`;

const CreateTaskLink = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${buttonColorNew};
  cursor: pointer;
  border-bottom: 1px solid ${colorLightGray};
  &:hover {
    background: #f0f4f8;
  }
`;

const TaskListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  ${scrollbarStyles}
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  cursor: pointer;
  background: #f8f9fa;
  border-bottom: 1px solid ${colorLightGray};
  user-select: none;
  &:hover {
    background: #f0f2f4;
  }
`;

const CategoryCheckbox = styled.input`
  width: 12px;
  height: 12px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: ${buttonColorNew};
`;

const CategoryName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${colorTextNew};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CategoryCount = styled.span`
  font-size: 11px;
  color: ${colorAdditional};
  background: #e9ecef;
  border-radius: 10px;
  padding: 1px 7px;
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 28px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;

const TaskCheckbox = styled.input`
  width: 12px;
  height: 12px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: ${buttonColorNew};
`;

const TaskItemLabel = styled.label`
  font-size: 12px;
  color: ${colorTextNew};
  cursor: pointer;
  margin: 0;
  flex: 1;
`;

const TaskCountBadge = styled.span`
  font-size: 11px;
  background: ${buttonColorNew};
  color: white;
  border-radius: 10px;
  padding: 2px 10px;
  margin-right: 4px;
`;

const SequenceColumnHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px;
  font-size: 10px;
  font-weight: 600;
  color: ${colorAdditional};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1px solid ${colorLightGray};
  background: #f8f9fa;
  gap: 8px;
  flex-shrink: 0;
`;

const SequenceScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  ${scrollbarStyles}
`;

const SequenceItem = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
  background: ${(props) => (props.isDragging ? "#f0f4f8" : "white")};
  box-shadow: ${(props) =>
    props.isDragging ? "0 2px 8px rgba(0,0,0,0.12)" : "none"};
  position: relative;
`;

const SequenceRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;

const DragHandle = styled.div`
  color: ${colorGray};
  cursor: grab;
  font-size: 12px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-top: 2px;
  &:active {
    cursor: grabbing;
  }
`;

const SequenceNumber = styled.span`
  font-size: 12px;
  color: ${colorAdditional};
  width: 24px;
  flex-shrink: 0;
  text-align: right;
  padding-top: 1px;
`;

const SequenceTaskName = styled.span`
  font-size: 13px;
  color: ${colorTextNew};
`;

const SequencePartsCol = styled.span`
  font-size: 12px;
  color: ${colorTextNew};
  width: 130px;
  flex-shrink: 0;
  //padding-top: 1px;
  align-self: center;
`;

const SequenceLaborCol = styled.span`
  font-size: 12px;
  color: ${colorTextNew};
  width: 110px;
  flex-shrink: 0;
  //padding-top: 1px;
  align-self: center;
`;

const EmptyText = styled.p`
  font-size: 13px;
  color: ${colorAdditional};
  padding: 16px;
  margin: 0;
`;

const DragHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 11px;
  color: ${colorAdditional};
  border-top: 1px solid ${colorLightGray};
  flex-shrink: 0;
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  padding: 0 2px;
  cursor: pointer;
  color: ${colorAdditional};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  justify-content: center;
  &:hover {
    color: ${buttonColorNew};
  }
`;

const TaskExpandedSection = styled.div`
  padding: 12px 16px 14px 60px;
  background: #f8f9fa;
  border-top: 1px solid #f0f0f0;
`;

const TaskExpandedSubLabel = styled.p`
  font-size: 10px;
  font-weight: 600;
  color: ${colorAdditional};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 6px 0;
`;

const TaskExpandedEmpty = styled.p`
  font-size: 12px;
  color: ${colorAdditional};
  margin: 0 0 4px 0;
  font-style: italic;
`;

const TaskExpandedValue = styled.p`
  font-size: 13px;
  color: ${colorTextNew};
  margin: 0 0 4px 0;
`;

const EditPartsLaborLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  margin-top: 10px;
  cursor: pointer;
  font-size: 12px;
  color: ${buttonColorNew};
  &:hover {
    opacity: 0.8;
  }
`;

const SuggestedPartsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 4px;
`;

const SuggestedPartsHeaderCell = styled.th`
  font-size: 10px;
  font-weight: 400;
  color: ${colorAdditional};
  text-transform: uppercase;
  padding-bottom: 4px;
  text-align: left;
  padding-right: 16px;
`;

const SuggestedPartsDataCell = styled.td`
  font-size: 12px;
  padding: 4px 16px 4px 0;
  color: ${colorTextNew};
  border-top: 1px solid #eee;
`;
